import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import styled from 'styled-components';
import { bemGenerator, noop } from '@prisjakt/ui-base';
import { emptyObj } from '@regular_ui/lib/definitions';
import { Hideable } from '@regular_ui/lib/components/hideable';
import ArrowDownIcon from 'mdi-react/ChevronDownIcon';
import { nudgeUpHideableVariant } from '@prisjakt/customization_prisjakt/variants/hideable';
import './dropdown.scss';

const styles = bemGenerator('ui-dropdown', [
  'header',
  'title',
  'list',
  'list-item',
  'selected',
  'toggle-button',
  'option-button',
  'overlay',
  'sub-content',
]);

const ArrowDown = styled(ArrowDownIcon).attrs(p => ({
  color: p.theme.icons.callToAction,
}))``;

class Dropdown extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: false,
    };

    this.clickEvent = e => this.handleOutSideClick(e);
  }

  componentWillUnmount() {
    Dropdown.toggleClickListener(false);
  }

  static getDerivedStateFromProps = (props, state) => {
    if (props.close && state.expanded) {
      Dropdown.toggleClickListener(false);
      return { expanded: false };
    }
    return null;
  };

  static toggleClickListener(expanded) {
    if (expanded) {
      window.addEventListener('click', this.clickEvent);
    } else {
      window.removeEventListener('click', this.clickEvent);
    }
  }

  handleOutSideClick(e) {
    if (this.wrapperElement.contains(e.target)) {
      return;
    }

    this.close();
  }

  close() {
    window.removeEventListener('click', this.clickEvent);
    this.setState({ expanded: false });
  }

  onExpand(e, expanded) {
    e.preventDefault();
    Dropdown.toggleClickListener(!expanded);

    this.setState({
      expanded: !expanded,
    });
  }

  onSelect(e, option, expanded) {
    e.preventDefault();
    const { onSelect } = this.props;

    Dropdown.toggleClickListener(!expanded);

    this.setState({
      expanded: !expanded,
    });

    onSelect(option);
  }

  getIndependentPosition() {
    const { offset, offsetOrigin } = this.props;
    return {
      marginTop: offset.y,
      [offsetOrigin]: offset.x,
    };
  }

  render() {
    const {
      title,
      titleClass,
      options,
      type,
      children,
      width,
      headerPosition,
      before,
      after,
      divider,
      className,
      selected,
      ariaLabel,
      position,
    } = this.props;
    const { expanded } = this.state;

    const independent = type === 'select-box-independent-type';
    const selectBox =
      type === 'select-box-type' || type === 'select-box-independent-type';

    return (
      <div
        style={{ width }}
        ref={div => {
          this.wrapperElement = div;
        }}
        className={cn(className, styles.block.withModifier({ expanded }))}
        data-test={this.props['data-test']}
      >
        <div
          ref={el => {
            this.headerElement = el;
          }}
          className={styles.header.withModifier({
            expanded,
            independent,
            [type]: true,
            [headerPosition]: true,
          })}
        >
          <button
            type="button"
            className={styles['toggle-button'].withModifier({
              'allow-lowercase': independent,
            })}
            onClick={e => this.onExpand(e, expanded)}
            aria-label={ariaLabel}
          >
            <span
              className={`${styles.title.withModifier({
                expanded,
              })} ${titleClass} main-style`}
            >
              {title}
            </span>
            {type === 'select-box-type' && (
              <span className={styles.selected}>{selected}</span>
            )}
            {!independent && (
              <ArrowDown
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            )}
          </button>
        </div>

        {selectBox && (
          <Hideable variant={nudgeUpHideableVariant} visible={expanded}>
            <ul
              style={independent ? this.getIndependentPosition() : emptyObj}
              className={styles.list.withModifier({
                expanded: true,
                independent,
                divider,
                [position]: true,
              })}
            >
              {before && <li className={styles['list-item']}>{before}</li>}

              {options.map(option => (
                <li
                  key={option.name + option.value}
                  className={styles['list-item']}
                  data-test="DropdownOption"
                  data-test-value={option.value}
                >
                  <button
                    type="button"
                    className={styles['option-button']}
                    onClick={e => this.onSelect(e, option, expanded)}
                  >
                    {option.name}
                  </button>
                </li>
              ))}

              {after && <li className={styles['list-item']}>{after}</li>}
            </ul>
          </Hideable>
        )}

        {type === 'custom-content-type' && (
          <div className={styles['sub-content'].withModifier({ expanded })}>
            {children}
          </div>
        )}
      </div>
    );
  }
}

const { string, arrayOf, func, shape, node, oneOf, bool, number } = PropTypes;
Dropdown.propTypes = {
  title: node,
  type: oneOf([
    'select-box-type',
    'select-box-independent-type',
    'custom-content-type',
  ]),
  headerPosition: oneOf(['center', 'left', 'right']),
  options: arrayOf(shape({ name: string, value: string })),
  width: string,
  onSelect: func,
  selected: string,
  children: node,
  before: node,
  after: node,
  titleClass: string,
  close: bool,
  offset: shape({ x: number, y: number }),
  offsetOrigin: oneOf(['left', 'right']),
  divider: bool,
  className: string,
  ariaLabel: string,
  'data-test': string,
  position: oneOf(['left', 'right']),
};

Dropdown.defaultProps = {
  title: null,
  type: 'select-box-type',
  headerPosition: 'left',
  options: [],
  width: 'auto',
  onSelect: noop,
  selected: null,
  children: null,
  before: null,
  after: null,
  titleClass: '',
  close: null,
  offset: { x: 0, y: 0 },
  offsetOrigin: 'left',
  divider: false,
  className: null,
  ariaLabel: null,
  'data-test': undefined,
  position: 'right',
};

export default Dropdown;
